import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
	type ReactNode
} from 'react'
import { AlertTriangle } from 'lucide-react'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog'
import { cn } from '@/lib/utils'

export type DeviceIntegritySignal =
	| 'jailbroken'
	| 'rooted'
	| 'hooking_framework'
	| 'external_storage'
	| 'mock_location'
	| 'adb_enabled'
	| 'developer_mode'
	| 'debugger_attached'

export type DeviceIntegrityPlatform = 'ios' | 'android' | 'other'

export interface DeviceIntegrity {
	compromised: boolean
	signals: DeviceIntegritySignal[]
	platform: DeviceIntegrityPlatform
	checkedAt: number
}

declare global {
	interface Window {
		__SECH_DEVICE_INTEGRITY__?: DeviceIntegrity
	}
}

const DEVICE_INTEGRITY_EVENT = 'sech:device-integrity'

const SIGNAL_DESCRIPTIONS: Record<DeviceIntegritySignal, string> = {
	jailbroken: 'iOS jailbreak detected',
	rooted: 'Android root access detected',
	hooking_framework: 'Code-hooking framework detected (e.g. Frida, Xposed)',
	external_storage: 'App installed on external storage',
	mock_location: 'Mock-location provider enabled',
	adb_enabled: 'Android Debug Bridge (ADB) is enabled',
	developer_mode: 'Developer mode is enabled',
	debugger_attached: 'A debugger is attached to the app'
}

interface DeviceIntegrityContextType {
	integrity: DeviceIntegrity | null
	isCompromised: boolean
	requireAcknowledgement: (actionLabel?: string) => Promise<boolean>
}

const DeviceIntegrityContext = createContext<DeviceIntegrityContextType | null>(
	null
)

const readInitialIntegrity = (): DeviceIntegrity | null => {
	if (typeof window === 'undefined') return null
	return window.__SECH_DEVICE_INTEGRITY__ ?? null
}

export const DeviceIntegrityProvider = ({
	children
}: {
	children: ReactNode
}) => {
	const [integrity, setIntegrity] = useState<DeviceIntegrity | null>(
		readInitialIntegrity
	)
	const [modalState, setModalState] = useState<{
		open: boolean
		actionLabel?: string
	}>({ open: false })
	const resolverRef = useRef<((proceed: boolean) => void) | null>(null)

	useEffect(() => {
		const handler = (event: Event) => {
			const detail = (event as CustomEvent<DeviceIntegrity>).detail
			if (detail) setIntegrity(detail)
		}
		window.addEventListener(DEVICE_INTEGRITY_EVENT, handler as EventListener)

		// Re-read in case the bridge injected the global after this provider mounted.
		if (window.__SECH_DEVICE_INTEGRITY__) {
			setIntegrity(window.__SECH_DEVICE_INTEGRITY__)
		}

		return () =>
			window.removeEventListener(
				DEVICE_INTEGRITY_EVENT,
				handler as EventListener
			)
	}, [])

	const isCompromised = !!integrity?.compromised

	const requireAcknowledgement = useCallback(
		(actionLabel?: string): Promise<boolean> => {
			// Always re-read the global at call time so we honour the latest signal
			// from the native bridge, even if a re-injection raced the React state.
			const current = window.__SECH_DEVICE_INTEGRITY__ ?? integrity
			if (!current?.compromised) return Promise.resolve(true)

			return new Promise<boolean>((resolve) => {
				resolverRef.current = resolve
				setModalState({ open: true, actionLabel })
			})
		},
		[integrity]
	)

	const handleResolve = (proceed: boolean) => {
		setModalState({ open: false })
		const resolver = resolverRef.current
		resolverRef.current = null
		if (resolver) resolver(proceed)
	}

	return (
		<DeviceIntegrityContext.Provider
			value={{ integrity, isCompromised, requireAcknowledgement }}
		>
			{children}
			<DeviceIntegrityWarningModal
				open={modalState.open}
				actionLabel={modalState.actionLabel}
				signals={integrity?.signals ?? []}
				onCancel={() => handleResolve(false)}
				onConfirm={() => handleResolve(true)}
			/>
		</DeviceIntegrityContext.Provider>
	)
}

export const useDeviceIntegrity = () => {
	const ctx = useContext(DeviceIntegrityContext)
	if (!ctx) {
		throw new Error(
			'useDeviceIntegrity must be used within a DeviceIntegrityProvider'
		)
	}
	return ctx
}

interface DeviceIntegrityWarningModalProps {
	open: boolean
	actionLabel?: string
	signals: DeviceIntegritySignal[]
	onCancel: () => void
	onConfirm: () => void
}

const DeviceIntegrityWarningModal = ({
	open,
	actionLabel,
	signals,
	onCancel,
	onConfirm
}: DeviceIntegrityWarningModalProps) => {
	const action = actionLabel ?? 'this action'
	const uniqueSignals = Array.from(new Set(signals))

	return (
		<AlertDialog
			open={open}
			onOpenChange={(next) => {
				if (!next) onCancel()
			}}
		>
			<AlertDialogContent
				className={cn(
					'rounded-2xl shadow-lg border fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 border-gray-200 p-6 bg-[#18181B]',
					'w-[90%] sm:w-full max-w-sm',
					'mx-auto sm:my-0'
				)}
			>
				<AlertDialogHeader className="space-y-3">
					<div className="flex items-center gap-2">
						<AlertTriangle className="h-5 w-5 text-[#EF4444]" />
						<AlertDialogTitle className="text-lg sm:text-xl font-semibold text-white">
							Device integrity warning
						</AlertDialogTitle>
					</div>
					<AlertDialogDescription className="text-sm text-[#BABABB] text-left">
						This device shows signs that may put {action} at risk. Continuing
						could expose your account, wallet keys, or payment details to
						malicious software.
					</AlertDialogDescription>
				</AlertDialogHeader>

				{uniqueSignals.length > 0 && (
					<ul className="mt-2 space-y-1 text-left text-xs text-[#BABABB]">
						{uniqueSignals.map((signal) => (
							<li key={signal} className="flex items-start gap-2">
								<span aria-hidden className="mt-[6px] h-1 w-1 rounded-full bg-[#EF4444]" />
								<span>{SIGNAL_DESCRIPTIONS[signal] ?? signal}</span>
							</li>
						))}
					</ul>
				)}

				<AlertDialogFooter className="flex flex-col sm:flex-row justify-between gap-3">
					<AlertDialogCancel
						onClick={onCancel}
						className="w-full sm:w-auto bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-5 py-2 rounded-lg transition-colors"
					>
						Cancel
					</AlertDialogCancel>
					<AlertDialogAction
						onClick={(e) => {
							e.preventDefault()
							onConfirm()
						}}
						className="w-full sm:w-auto text-white font-medium px-5 py-2 rounded-lg transition-colors bg-red-600 hover:bg-red-500"
					>
						Continue anyway
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	)
}
